import * as Amplitude from "@amplitude/analytics-browser";
import { plugin as engagementPlugin } from "@amplitude/engagement-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import UserService from "services/UserService";
import { EventNames, EVENTS, PROPERTIES } from "./AnalyticsService.config";

const apiKey = process.env.REACT_APP_AMPLITUDE_API_KEY;

const initConfig: Amplitude.Types.BrowserOptions = {
  defaultTracking: {
    fileDownloads: false, // Disable automatic tracking of file downloads
    pageViews: true, // Enable automatic tracking of page views
    formInteractions: false, // Disable automatic tracking of form interactions
    sessions: true, // Session tracking is required for session replay, so we keep it enabled
    attribution: true,
  },
};

const IDENTIFY_EVENT_TYPES = ["$identify", "$groupidentify"];
const SCREEN_DIMENSIONS_PLUGIN_NAME = "screen-dimensions";

const getScreenDimensionProperties = () => {
  const { width: screenWidth, height: screenHeight } = window.screen;
  const { innerWidth: viewportWidth, innerHeight: viewportHeight } = window;

  return {
    [PROPERTIES.SCREEN_WIDTH]: screenWidth,
    [PROPERTIES.SCREEN_HEIGHT]: screenHeight,
    [PROPERTIES.SCREEN_RESOLUTION]: `${screenWidth}x${screenHeight}`,
    [PROPERTIES.VIEWPORT_WIDTH]: viewportWidth,
    [PROPERTIES.VIEWPORT_HEIGHT]: viewportHeight,
    [PROPERTIES.VIEWPORT_SIZE]: `${viewportWidth}x${viewportHeight}`,
  };
};

// Read at send time so viewport values reflect the window size when each event fires
const screenDimensionsPlugin: Amplitude.Types.EnrichmentPlugin = {
  name: SCREEN_DIMENSIONS_PLUGIN_NAME,
  type: "enrichment",
  execute: async (event) => {
    if (IDENTIFY_EVENT_TYPES.includes(event.event_type)) {
      return event;
    }

    return {
      ...event,
      event_properties: { ...event.event_properties, ...getScreenDimensionProperties() },
    };
  },
};

/**
 * AnalyticsService is a singleton class that manages analytics tracking using Amplitude.
 */
class AnalyticsService {
  private static eventQueue: Array<{ name: EventNames; properties?: Record<string, any> }> = [];
  private static isInitialized = false;
  private static APPLICATION = "HUB_V1_WEB";
  private static sessionReplayPlugin: ReturnType<typeof sessionReplayPlugin> | null = null;
  private static engagementPlugin: ReturnType<typeof engagementPlugin> | null = null;
  static PROPERTIES = PROPERTIES;
  static EVENTS = EVENTS;

  /**
   * Initialize Amplitude with plugins
   */
  static async init() {
    if (process.env.REACT_APP_BUILD_ENVIRONMENT === "Development") {
      return;
    }

    if (AnalyticsService.isInitialized) {
      console.warn("AnalyticsService already initialized");
      return;
    }

    if (!apiKey) {
      console.warn("REACT_APP_AMPLITUDE_API_KEY not found");
      return;
    }

    // Step 1: Add Session Replay plugin BEFORE initialization
    // Rules are set via remote config in Amplitude dashboard, but we need to add the plugin here to ensure it loads correctly
    AnalyticsService.sessionReplayPlugin = sessionReplayPlugin();

    Amplitude.add(AnalyticsService.sessionReplayPlugin);

    // Step 1b: Add Guides & Surveys plugin BEFORE initialization (same requirement as session replay)
    AnalyticsService.engagementPlugin = engagementPlugin();

    Amplitude.add(AnalyticsService.engagementPlugin);

    Amplitude.add(screenDimensionsPlugin);

    // Step 2: Initialize Amplitude
    Amplitude.init(apiKey, initConfig);

    // Step 3: Set user properties if UserService is available
    if (UserService.currentUser) {
      try {
        const organisationId = await UserService.getOrganisationId();
        const { uid: userId, email } = UserService.currentUser;

        if (organisationId && userId && email) {
          const identifyEvent = new Amplitude.Identify()
            .setOnce(AnalyticsService.PROPERTIES.USER_PROPERTY_ORGANISATION_ID, organisationId as string)
            .set(AnalyticsService.PROPERTIES.USER_PROPERTY_EMAIL, email)
            .setOnce(AnalyticsService.PROPERTIES.USER_PROPERTY_FIRST_SEEN, new Date().toISOString())
            .set(AnalyticsService.PROPERTIES.USER_PROPERTY_LAST_SEEN, new Date().toISOString());

          Amplitude.setUserId(userId);
          Amplitude.identify(identifyEvent);
        }
      } catch (error) {
        console.error("Failed to set user properties:", error);
      }
    }

    // Overwritten on every init so the user profile reflects the latest screen and viewport
    const screenDimensionsIdentify = Object.entries(getScreenDimensionProperties()).reduce(
      (identify, [property, value]) => identify.set(property, value),
      new Amplitude.Identify()
    );

    Amplitude.identify(screenDimensionsIdentify);

    AnalyticsService.isInitialized = true;

    // Step 4: Flush queued events
    if (AnalyticsService.eventQueue.length > 0) {
      AnalyticsService.eventQueue.forEach(({ name, properties }) => {
        AnalyticsService.logEvent(name, properties);
      });
      AnalyticsService.eventQueue = [];
    }
  }

  /**
   * Log an analytics event
   */
  static logEvent(eventName: EventNames, eventProperties?: Record<string, any>, page?: string) {
    // Capture top-level path as page, e.g. "calendar" from "/calendar/events"
    const topLevelPage = window.location.pathname.split("/")[1] || undefined;

    const properties = {
      application: AnalyticsService.APPLICATION,
      page: page || topLevelPage,
      ...eventProperties,
    };

    if (AnalyticsService.isInitialized) {
      Amplitude.track(eventName, properties);

      if (process.env.REACT_APP_DEBUG_ANALYTICS && JSON.parse(process.env.REACT_APP_DEBUG_ANALYTICS || "false")) {
        console.log("[EVENT]:", eventName, JSON.stringify(properties, null, 2));
      }
    } else {
      // Queue events that happen before initialization
      AnalyticsService.eventQueue.push({ name: eventName, properties: properties });
    }
  }

  /**
   * Get Amplitude instance for advanced usage
   */
  static getAmplitudeInstance() {
    return Amplitude;
  }

  /**
   * Check if analytics is initialized
   */
  static getIsInitialized() {
    return AnalyticsService.isInitialized;
  }

  /**
   * Clear analytics state and cleanup
   */
  static clear() {
    if (AnalyticsService.sessionReplayPlugin && AnalyticsService.sessionReplayPlugin.name) {
      Amplitude.remove(AnalyticsService.sessionReplayPlugin.name);
      AnalyticsService.sessionReplayPlugin = null;
    }

    if (AnalyticsService.engagementPlugin && AnalyticsService.engagementPlugin.name) {
      Amplitude.remove(AnalyticsService.engagementPlugin.name);
      AnalyticsService.engagementPlugin = null;
    }

    Amplitude.remove(SCREEN_DIMENSIONS_PLUGIN_NAME);

    Amplitude.reset();
    AnalyticsService.isInitialized = false;
    AnalyticsService.eventQueue = [];
  }
}

export default AnalyticsService;
